import { useContext, useEffect } from 'react';
import { StepActiveContext, StepNavContext, type StepNav } from './stepNavContext';

/**
 * Registers the calling step's navigation config (onNext, canGoNext, labels)
 * with the enclosing ExpauseStepper's shared footer. The step must be rendered
 * inside an ExpauseStepper with `showNavigation` enabled.
 *
 * Registration is gated by StepActiveContext — only the active step publishes
 * its nav, so inactive steps kept alive by slide/keepAlive animations don't
 * overwrite the footer.
 *
 * Deps control when the registration updates — include any reactive fields
 * referenced inside `nav` (e.g. canGoNext, handlers closing over step state).
 */
export const useRegisterStepNav = (nav: StepNav, deps: React.DependencyList): void => {
  const setNav = useContext(StepNavContext);
  const isActive = useContext(StepActiveContext);

  useEffect(() => {
    if (!setNav || !isActive) return;
    setNav(nav);
    return () => setNav(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNav, isActive, ...deps]);
};
